import React, { useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { Project } from "../../types/Work";

interface ProjectModalProps {
  show: boolean;
  onHide: () => void;
  onSave: (project: Omit<Project, "id" | "createdAt" | "updatedAt">) => void;
  project?: Project;
}

const PRESET_COLORS = [
  "#007bff", // blue
  "#28a745", // green
  "#dc3545", // red
  "#ffc107", // yellow
  "#17a2b8", // cyan
  "#6f42c1", // purple
  "#fd7e14", // orange
  "#20c997", // teal
];

export const ProjectModal: React.FC<ProjectModalProps> = ({
  show,
  onHide,
  onSave,
  project,
}) => {
  const [name, setName] = useState(project?.name || "");
  const [color, setColor] = useState(project?.color || PRESET_COLORS[0]);
  const [description, setDescription] = useState(project?.description || "");

  React.useEffect(() => {
    if (project) {
      setName(project.name);
      setColor(project.color);
      setDescription(project.description || "");
    } else {
      setName("");
      setColor(PRESET_COLORS[0]);
      setDescription("");
    }
  }, [project, show]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    onSave({
      name: name.trim(),
      color,
      description: description.trim(),
      ownerId: "user-1", // Mock user ID
    });

    onHide();
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>{project ? "Edit Project" : "Create Project"}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Project Name *</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter project name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoFocus
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Color *</Form.Label>
            <div className="d-flex gap-2 flex-wrap mb-2">
              {PRESET_COLORS.map((presetColor) => (
                <div
                  key={presetColor}
                  onClick={() => setColor(presetColor)}
                  style={{
                    width: "40px",
                    height: "40px",
                    backgroundColor: presetColor,
                    borderRadius: "4px",
                    cursor: "pointer",
                    border:
                      color === presetColor
                        ? "3px solid #000"
                        : "2px solid #ddd",
                  }}
                  title={presetColor}
                />
              ))}
            </div>
            <Form.Control
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Enter project description (optional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={!name.trim()}>
            {project ? "Update" : "Create"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};
